import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

/**
 * Top-level error boundary. Without it, a single render-loop crash (React error
 * #185, "too many re-renders") unmounts the entire tree and leaves operators
 * staring at a black screen with every in-flight request cancelled. Instead we
 * trap the error, surface a recoverable card, and log the failing component
 * stack to the console so the next occurrence is diagnosable from the minified
 * production bundle (source maps are emitted by the production build).
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  // Guard so a single crash reports exactly once even if React re-invokes the
  // lifecycle while tearing the failed subtree down.
  private reported = false;

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const componentStack = info.componentStack ?? null;
    this.setState({ componentStack });
    // Log to the console (and any global error reporter) so the crashing
    // component name survives in production logs. Source maps make this stack
    // readable; never swallow it silently.
    console.error(
      "[ErrorBoundary] Uncaught render error:",
      error,
      "\nComponent stack:",
      componentStack,
    );
    // The browser console is the ONLY place a client render crash (e.g. React
    // #185) surfaces, so a production-only loop otherwise leaves no server
    // trace. Report it to the API so the failing component shows up in the
    // deployment logs and can be diagnosed without reproducing it locally.
    if (!this.reported) {
      this.reported = true;
      void this.reportToServer(error, componentStack);
    }
  }

  private async reportToServer(error: Error, componentStack: string | null) {
    try {
      await fetch("/api/client-errors", {
        method: "POST",
        headers: { "content-type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          message: error.message,
          name: error.name,
          stack: error.stack ?? null,
          componentStack,
          url: typeof window !== "undefined" ? window.location.href : null,
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : null,
          at: new Date().toISOString(),
        }),
      });
    } catch {
      /* best-effort: error reporting must never throw */
    }
  }

  handleReset = () => {
    this.reported = false;
    this.setState({ error: null, componentStack: null });
  };

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background p-4">
          <div className="max-w-md w-full p-8 border border-border bg-card rounded-lg shadow-xl text-center space-y-4">
            <AlertTriangle className="h-10 w-10 mx-auto text-amber-400" />
            <h1 className="text-xl font-medium text-foreground">
              Something went wrong
            </h1>
            <p className="text-sm text-secondary-foreground">
              The page hit an unexpected error and couldn't continue. Your data
              is safe — reloading usually clears it.
            </p>
            {this.state.error.message && (
              <p className="text-xs text-muted-foreground font-mono break-words">
                {this.state.error.message}
              </p>
            )}
            {this.state.componentStack && (
              <details className="text-left">
                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                  Show technical details
                </summary>
                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-2 text-[10px] leading-snug text-muted-foreground">
                  {this.state.componentStack}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              <Button className="flex-1" onClick={this.handleReset}>
                Try again
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => window.location.reload()}
              >
                Reload
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
